import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, Camera, MapPin, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const PORTAL_TOKEN = () => localStorage.getItem('portal_token');

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  } catch { return iso; }
};

const PortalClockRecords = () => {
  const [data, setData] = useState({ sessions: [], is_super_admin: false });
  const [missed, setMissed] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const past = new Date(Date.now() - 14 * 86400 * 1000).toISOString().slice(0, 10);
  const [start, setStart] = useState(past);
  const [end, setEnd] = useState(today);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${PORTAL_TOKEN()}` };
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/api/employee-portal/clock-records?start_date=${start}&end_date=${end}`, { headers }),
        axios.get(`${API}/api/employee-portal/missed-clockouts`, { headers }),
      ]);
      setData(r1.data || { sessions: [] });
      setMissed(r2.data?.rows || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.sessions.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.employee_name || '').toLowerCase().includes(q)
      || (s.employee_code || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4 p-4 max-w-5xl mx-auto" data-testid="portal-clock-records">
      <div className="flex items-center gap-2">
        <Clock className="h-6 w-6 text-violet-600" />
        <div>
          <h1 className="text-xl font-bold text-gray-900">Clock In/Out Records</h1>
          <p className="text-xs text-gray-500">
            {data.is_super_admin ? 'Viewing all employees (Super Admin)' : 'Viewing your own records'}
          </p>
        </div>
      </div>

      {/* Missed clock-outs banner */}
      {missed.length > 0 && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-rose-800 font-semibold">
              <AlertTriangle className="h-5 w-5" />
              {data.is_super_admin ? `${missed.length} missed clock-out${missed.length > 1 ? 's' : ''} (>12h)` : 'You have a missed clock-out'}
            </div>
            <div className="space-y-1">
              {missed.map(r => (
                <div key={r.event_id} className="text-xs flex items-center justify-between bg-white rounded px-2 py-1.5">
                  <div>
                    <span className="font-semibold">{r.employee_name}</span>
                    <span className="text-gray-500 font-mono ml-1">· {r.employee_code}</span>
                    <span className="text-gray-500 ml-1">@ {r.branch_name}</span>
                  </div>
                  <span className="text-rose-700 font-bold">{r.elapsed_hours}h elapsed</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[11px] text-gray-500 block">From</label>
            <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="w-40 h-8 text-sm" data-testid="records-from" />
          </div>
          <div>
            <label className="text-[11px] text-gray-500 block">To</label>
            <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="w-40 h-8 text-sm" data-testid="records-to" />
          </div>
          {data.is_super_admin && (
            <div className="flex-1 min-w-[180px]">
              <label className="text-[11px] text-gray-500 block">Search</label>
              <Input placeholder="Name or employee code…" value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm" data-testid="records-search" />
            </div>
          )}
          <p className="text-xs text-gray-500 ml-auto self-center">{filtered.length} sessions</p>
        </CardContent>
      </Card>

      {/* Sessions list */}
      {loading ? (
        <div className="text-center text-gray-500 py-12 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">No clock records in this range.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => (
            <Card
              key={s.in_event_id}
              className={s.is_missed ? 'border-rose-300 bg-rose-50/40' : (s.is_open ? 'border-amber-300 bg-amber-50/30' : '')}
              data-testid={`session-${s.in_event_id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-gray-900">
                      {s.employee_name}
                      <span className="text-[11px] text-gray-400 font-mono ml-2">{s.employee_code}</span>
                      {s.is_missed && <Badge className="bg-rose-100 text-rose-700 ml-2 text-[10px]">Missed clock-out</Badge>}
                      {s.is_open && !s.is_missed && <Badge className="bg-amber-100 text-amber-700 ml-2 text-[10px]">Still IN</Badge>}
                    </div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" /> {s.branch_name || '—'}
                      {s.has_selfie && <span className="ml-1 text-violet-600 inline-flex items-center gap-0.5"><Camera className="h-3 w-3" />selfie</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className={`font-bold font-mono ${s.is_missed ? 'text-rose-700' : 'text-violet-700'}`}>
                      {s.duration_hours}h
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div className="bg-emerald-50 rounded px-2 py-1.5">
                    <p className="text-[10px] uppercase text-emerald-700">Clocked In</p>
                    <p className="font-mono text-emerald-900">{fmt(s.clock_in)}</p>
                  </div>
                  <div className={`${s.is_open ? 'bg-amber-50' : 'bg-sky-50'} rounded px-2 py-1.5`}>
                    <p className="text-[10px] uppercase text-gray-600">Clocked Out</p>
                    <p className={`font-mono ${s.is_open ? 'text-amber-700 italic' : 'text-sky-900'}`}>
                      {s.is_open ? '— still IN —' : fmt(s.clock_out)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalClockRecords;
